import { Hls as Hlsjs } from 'global';
import { EVENT_ERROR } from '../constants';
import PlayerError from '../utils/errors';

const Hls = {};

const defaultOptions = {
  // xhrSetup: function (xhr, url) {
  //   xhr.withCredentials = true; // do send cookie
  // }
};

/**
 * Init an hls.js instance on a player
 *
 * @param {Object} player   A player instance
 */
Hls.init = function (player) {
  if (!Hls.isSupported()) {
    return;
  }

  const options = Object.assign({}, defaultOptions, player._options.hlsjs);

  if (options instanceof Hlsjs) {
    player._hlsjs = options;
  } else if (Hlsjs) {
    player._hlsjs = new Hlsjs(options);
  }

  // mark the player as live when a live hls stream is loaded
  player._hlsjs.on(Hlsjs.Events.LEVEL_LOADED, function hlsLevelLoaded (event, data) {
    player._isLive = data.details.live;
  });

  // log hls.js warnings and errors
  player._hlsjs.on(Hlsjs.Events.ERROR, function hlsError (event, data) {
    const errorType = data.type;
    const errorDetails = data.details;
    const errorFatal = data.fatal;
    const errorMessage = data.err && data.err.message;

    player._logger.warn(`[Player] HLS Error: ${errorType} (${errorDetails}) - ${errorMessage}`);

    if (errorFatal) {
      let error;
      switch (errorType) {
        case Hlsjs.ErrorTypes.NETWORK_ERROR:
          error = new PlayerError(PlayerError.NETWORK);
          break;
        default:
          error = new PlayerError(PlayerError.SRC_NOT_SUPPORTED);
          break;
      }

      player.handleEvent({
        type: EVENT_ERROR,
        error: error
      });

      Hls.stopLoad(player);
    }
  });
};

/**
 * Load a source for playback
 *
 * @param {Object} player A player instance
 * @param {Object} source The source being loaded
 * @param {Boolean} startLoad Whether to begin loading audio data or wait for play to be called
 */
Hls.loadSource = function (player, source, startLoad = false) {
  Hls.stopLoad(player);

  if (startLoad) {
    Hls.startLoad(player);
  }
};

/**
 * Start loading audio data from the remote source
 *
 * @param {Object} player A player instance
 */
Hls.startLoad = function (player) {
  if (Hls.existsOn(player)) {
    Hls.destroy(player);
    Hls.init(player);
  }

  Hls.attach(player);

  // once the hls.js instance has been attached, begin loading of audio data
  player._hlsjs.once(Hlsjs.Events.MEDIA_ATTACHED, function () {
    const playbackUrl = player.computed.replace(player.getCurrentSource(), encodeURIComponent);
    player._hlsjs.loadSource(playbackUrl);
    player._hlsjs.startLoad();
  });
};

/**
 * Empty the current hls source on a player instance
 *
 * @param {Object} player A player instance
 */
Hls.emptySource = function (player) {
  Hls.stopLoad(player);
};

/**
 * Stop loading current hls audio data
 *
 * @param {Object} player A player instance
 */
Hls.stopLoad = function (player) {
  player._hlsjs.stopLoad();

  if (Hls.isAttached(player)) {
    Hls.detach(player);
  }
};

/**
 * Attach an hls.js instance to a player instance
 *
 * @param {Object} player A player instance
 */
Hls.attach = function (player) {
  player._hlsjs.attachMedia(player._media);
};

/**
 * Detach an hls.js instance from a player instance
 *
 * @param {Object} player A player instance
 */
Hls.detach = function (player) {
  player._hlsjs.detachMedia();
};

/**
 * Check if an hls.js instance has been attached to a player instance
 *
 * @param {Object} player A player instance
 *
 * @return {Boolean}
 */
Hls.isAttached = function (player) {
  return player._hlsjs && player._hlsjs.media;
};

/**
 * Check if an hls.js instance has been initialized for a player instance
 *
 * @param {Object} player A player instance
 *
 * @return {Boolean}
 */
Hls.existsOn = function (player) {
  return !!player._hlsjs;
};

/**
 * Destroy an hls.js instance on a player instance
 *
 * @param {Object} player A player instance
 */
Hls.destroy = function (player) {
  if (Hls.existsOn(player)) {
    player._hlsjs.destroy();
    player._hlsjs = false;
  }
};

/**
 * Determines whether or not HLS.js is supported in the current environment.
 * This test will exclude environments where HLS playback is supported natively.
 */
Hls.isSupported = function () {
  const mediaTest = document.createElement('audio');

  // test for native HLS playback support
  if (mediaTest.canPlayType('application/vnd.apple.mpegurl').match(/probably|maybe/)) {
    return false;
  }

  // test for hls.js playback support
  if (Hlsjs && Hlsjs.isSupported()) {
    return true;
  }
  return false;
};

export default Hls;
